import type { CSSProperties, FC, ReactNode } from 'react'

interface PracticeSketchProps {
    sketch: 'retrofit' | 'massing'
    label: string
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

/**
 * Small line drawings that draw themselves in when their parent block switches data-reveal to "in".
 */
export const PracticeSketch: FC<PracticeSketchProps> = ({ sketch, label }): ReactNode => {
    if (sketch === 'retrofit') {
        const windows: Array<[number, number]> = []

        for (let row = 0; row < 3; row++) {
            for (let column = 0; column < 4; column++) windows.push([92 + column * 58, 148 + row * 62])
        }

        const survey: Array<[number, number]> = [[60, 340], [60, 120], [200, 40], [340, 120], [340, 340], [150, 340], [250, 340], [200, 122]]

        return (
            <svg viewBox='0 0 400 380' className='h-full w-full' role='img' aria-label={label}>
                <g fill='none' stroke='#ECE6D8' strokeWidth={1.2} strokeLinejoin='miter'>
                    <path d='M60 340 V120 L200 40 L340 120 V340 Z' pathLength={1} className='draw-path' style={delay(80)} />
                    <path d='M30 340 H370' pathLength={1} className='draw-path' style={delay(40)} />
                </g>

                <g fill='none' stroke='#8A8478' strokeWidth={0.8}>
                    {windows.map(([x, y], index) => (
                        <path key={`${x}-${y}`} d={`M${x} ${y} h36 v44 h-36 Z`} pathLength={1} className='draw-path' style={delay(260 + index * 40)} />
                    ))}
                    <path d='M186 340 v-58 h28 v58' pathLength={1} className='draw-path' style={delay(520)} />
                </g>

                <g fill='none' stroke='#86B6F0' strokeWidth={1}>
                    <path d='M150 148 h36 v44 h-36 Z M150 148 L186 192 M186 148 L150 192' pathLength={1} className='draw-path' style={delay(900)} />
                    <path d='M254 214 h36 v44 h-36 Z' pathLength={1} className='draw-path' style={delay(980)} />
                </g>

                <g className='reveal-cell' style={delay(1100)}>
                    {survey.map(([x, y]) => (
                        <g key={`${x}-${y}`}>
                            <rect x={x - 3} y={y - 3} width={6} height={6} fill='none' stroke='#86B6F0' strokeWidth={1} />
                            <path d={`M${x - 7} ${y} h4 M${x + 3} ${y} h4 M${x} ${y - 7} v4 M${x} ${y + 3} v4`} stroke='#86B6F0' strokeWidth={0.8} />
                        </g>
                    ))}

                    <text x={196} y={180} fill='#86B6F0' className='font-mono text-[10px] tracking-[0.1em]'>
                        R-01 LINTEL
                    </text>

                    <text x={296} y={246} fill='#86B6F0' className='font-mono text-[10px] tracking-[0.1em]'>
                        R-02 SILL
                    </text>

                    <text x={60} y={366} fill='#8A8478' className='font-mono text-[10px] tracking-[0.1em]'>
                        8 CONTROL POINTS / RMS 1.4 MM
                    </text>
                </g>
            </svg>
        )
    }

    const iso = (x: number, y: number, z: number): [number, number] => [200 + (x - z) * 0.4785, 250 + (x + z) * 0.275 - y * 0.55]

    const box = (x: number, z: number, w: number, d: number, h: number): string => {
        const corners = [
            iso(x, 0, z), iso(x + w, 0, z), iso(x + w, 0, z + d), iso(x, 0, z + d),
            iso(x, h, z), iso(x + w, h, z), iso(x + w, h, z + d), iso(x, h, z + d)
        ].map(([px, py]) => `${px.toFixed(1)} ${py.toFixed(1)}`)

        return `M${corners[4]} L${corners[5]} L${corners[6]} L${corners[7]} Z M${corners[4]} L${corners[0]} L${corners[1]} L${corners[5]} M${corners[1]} L${corners[2]} L${corners[6]} M${corners[0]} L${corners[3]} L${corners[7]}`
    }

    const options = [
        { path: box(-170, -30, 60, 60, 120), tag: 'A / TOWER / 410 T', at: iso(-170, 130, -30) },
        { path: box(-60, -60, 100, 80, 60), tag: 'B / BLOCK / 300 T', at: iso(-60, 70, -60) },
        { path: box(60, -20, 120, 40, 30), tag: 'C / LONG BAR / 260 T', at: iso(60, 40, -20) }
    ]

    return (
        <svg viewBox='0 0 400 380' className='h-full w-full' role='img' aria-label={label}>
            <path d={`M${iso(-180, 0, -110).join(' ')} L${iso(200, 0, -110).join(' ')} L${iso(200, 0, 60).join(' ')} L${iso(-180, 0, 60).join(' ')} Z`} fill='none' stroke='#26231F' strokeWidth={1} pathLength={1} className='draw-path' style={delay(40)} />

            {options.map((option, index) => (
                <g key={option.tag}>
                    <path d={option.path} fill='none' stroke={index === 2 ? '#86B6F0' : '#ECE6D8'} strokeWidth={1.1} strokeLinejoin='round' pathLength={1} className='draw-path' style={delay(200 + index * 260)} />

                    <text x={option.at[0]} y={option.at[1] - 8} fill={index === 2 ? '#86B6F0' : '#8A8478'} className='reveal-cell font-mono text-[10px] tracking-[0.08em]' style={delay(900 + index * 120)}>
                        {option.tag}
                    </text>
                </g>
            ))}

            <text x={20} y={366} fill='#8A8478' className='reveal-cell font-mono text-[10px] tracking-[0.1em]' style={delay(1200)}>
                CARBON BUDGET A1 TO A3 / OPTION C CARRIED
            </text>
        </svg>
    )
}
